import { useEffect, useRef } from 'react';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
    XIMI_NATIVE_POST_LOGOUT_URI,
    XIMI_NATIVE_REDIRECT_URI,
    ximiUserManager
} from './oidcConfig';
import { parseXimiProvisioningFromDeeplink } from './parseXimiProvisioning';
import { useXimiSSOAuth } from './useXimiSSOAuth';

const isXimiSignInCallback = (url: string): boolean => url.startsWith(XIMI_NATIVE_REDIRECT_URI);

/**
 * Native-only deeplinks (`fr.stimulin://ximi/…`). No-op on web.
 * - `/sso`: back from the SSO opened in the system browser (Android Custom Tab) →
 *   signinCallback (PKCE verifier read back from Preferences). Also read at launch
 *   (getLaunchUrl): Android may have killed the app while the Custom Tab was open.
 * - `/logout`: back from end_session (Android logout) → nothing to do.
 * - otherwise: Ximi provisioning (once Ximi web gives this scheme to the QR) → SSO login,
 *   same parser as the QR scan. The account is persisted AFTER a successful sign-in
 *   (usePersistXimiAccount), not here.
 */
export function useXimiDeeplink(): void {
    const ximiSSO = useXimiSSOAuth();
    // login est recréé à chaque render → ref pour ne s'abonner qu'une fois.
    const loginRef = useRef(ximiSSO.login);
    loginRef.current = ximiSSO.login;

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return undefined;

        App.getLaunchUrl().then(async (launch) => {
            if (launch && isXimiSignInCallback(launch.url)) {
                await ximiUserManager.signinCallback(launch.url);
            }
        });

        const handlePromise = App.addListener(
            'appUrlOpen',
            async ({ url }: URLOpenListenerEvent) => {
                if (isXimiSignInCallback(url)) {
                    await ximiUserManager.signinCallback(url);
                    return;
                }
                if (url.startsWith(XIMI_NATIVE_POST_LOGOUT_URI)) return; // back from end_session
                const provisioning = parseXimiProvisioningFromDeeplink(url);
                if (!provisioning) return; // pas un lien Ximi → on ignore
                const { tenantCode, loginHint } = provisioning;
                await loginRef.current({ tenantCode, loginHint });
            }
        );

        return () => {
            handlePromise.then((handle) => handle.remove());
        };
    }, []);
}
